import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import React, { useEffect, useMemo } from 'react';
import { IntersectionOptions } from 'react-intersection-observer';
import { AddToCartReferType } from '@/constants/track';
import useCart from '@/store/shopping/index';
import useProductCardInview from '@/tracks/hooks/useProductCardInView';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { StatusEnum } from '@/types/cart';
import { ProductMediaItem, ProductType } from '@/types/product';
import { InViewOptions, PositionInfoT2Param } from '@/types/track';
import { buildUrlWithOrigin, convertObjectToUrlParams, getCDNUrl, isSayWeeeApp, wrapCurrency } from '@/utils/tool';

import HorizontalCardInner from './horizontalCardInner';
import styles from './index.module.scss';
import VerticalCardInner from './verticalCardInner';
import { isMobile } from '@/utils/common/device';
import { getProductCardExtraAttrs } from '@/utils/productFormat';

export interface ProductCardProps {
  type: string;
  isHorizontal: boolean;
  changeSize: string;
  showFavorite: boolean;
  showActivity: boolean;
  showWeekly: boolean;
  showLeft: boolean;
  showMemberPrice: boolean;
  showBasePrice: boolean;
  showAddBtn: boolean;
  data: ProductType;
  isLink: boolean;
  skeleton?: React.ReactNode;
  skeletonFactor?: boolean;
  transformLink?: (item: ProductType) => string;
  className?: string;
  addCartParams: {
    source?: string;
    referType: string;
    deliveryDate: string;
    isInCart?: boolean;
    referValue?: string;
    positionInfoT2?: PositionInfoT2Param;
    ctx?: any;
    isAppSupportPurchaseStatusNotify?: boolean;
  };
  trackType: string;
  impressionOption?: InViewOptions | (() => InViewOptions);
  isInUpsell?: boolean;
  isRemain: boolean;
  miniTitle: boolean;
  isRelated?: boolean;
  preSell?: boolean;
  imageSize?: number;
  onATC?: () => void;
  fixedTitletHeight?: boolean;
  showUnitInfo?: boolean;
  showLabel?: boolean;
  isPromo?: boolean;
  bottomTip?: string | React.ReactNode;
  isTrending?: boolean;
  is_mkpl?: boolean;
  showTopTitle?: boolean;
  showTags?: boolean;
  cardSize?: string;
  countDownTimeStamp?: number;
  isLightningCard?: boolean;
  produtLink?: any;
  showAllTags?: boolean;
  isInPDP?: boolean;
  showBrandName?: boolean;
  isInProductCard?: boolean;
  positionInfoT2?: PositionInfoT2Param;
  supportPurchaseStatusNotify?: boolean;
  cardInnerCls?: string;
  cardHeadCls?: string;
  soldOutCls?: string;
  priceCls?: string;
  addCartCls?: string;
  trackSaveClick?: (targetCollect: boolean) => void;
  /** 元素在视窗位置发生变化回调 */
  onViewChange?: (props: {
    inView: boolean;
    play: () => void;
    stop: () => void;
    productInfo: ProductType;
    mediaItem: ProductMediaItem;
    cardElement?: HTMLDivElement;
    /** 触发来源  */
    trigger?: 'click' | 'auto';
  }) => void;
  viewOptions?: IntersectionOptions;
  isRobot?: boolean;
  trackDataInDialog?: Record<string, any>;
  showExtraTag?: boolean;
  //是否需要请求猜你喜欢
  fetchYouMayLike?: boolean;
  /** 需要给productCart跳转链接拼接的参数  */
  linkParams?: Record<string, any>;
  /** 是否展示marketplace的由xxx配送 */
  showSoldBy?: boolean;
  /* 使用新的加购接口(合并结算之后) */
  useNewUpdateCartApi?: boolean;
  isInCart?: boolean;
  isNeedFilterMaxLimit?: boolean;
  /**
   * 是否是秒杀页面
   */
  isLightningDeals?: boolean;
}

export const soldOutStatus = [StatusEnum?.sold_out as string];
const ProductCard: React.FC<Partial<ProductCardProps>> = props => {
  const {
    data,
    isHorizontal = false,
    isLink = true,
    skeleton,
    skeletonFactor = false,
    className,
    addCartParams,
    children,
    transformLink,
    trackType,
    isInUpsell = false,
    miniTitle = false,
    cardSize,
    isLightningCard,
    showActivity,
    showWeekly,
    showLabel = true,
    positionInfoT2,
    cardInnerCls,
    trackDataInDialog,
    linkParams = {}
  } = props;

  let [impressionOption, setImpressionOption] = React.useState<InViewOptions | (() => InViewOptions)>({});
  useEffect(() => {
    let option = typeof props.impressionOption === 'function' ? props.impressionOption() : props.impressionOption || {};
    setImpressionOption(option);
  }, [props.impressionOption]);
  //如果是直邮商品, referType全部改为pantry
  if ((data as any).is_pantry) {
    addCartParams.referType = AddToCartReferType.pantry;
  }

  // Global+, referType为seller
  if (data?.biz_type === 'seller') {
    addCartParams.referType = AddToCartReferType.seller;
    addCartParams.referValue = `${data?.vender_info_view?.vender_id}`;
  }

  const { lang } = useTranslation('common');
  const { id2qty } = useCart();

  const { saveClick, productClick } = useClickActionT2();

  if (!!positionInfoT2) {
    positionInfoT2.context = {
      ...positionInfoT2?.context,
      global_vendor: data?.biz_type === 'seller' ? `${data?.vender_id || ''}` : null
    };
  }

  addCartParams.positionInfoT2 = positionInfoT2;

  const impressionOptionNew = useMemo(() => {
    const impressionOptionT2 = {
      ...(positionInfoT2?.modSecPos || {}),
      co: {
        ...{
          ...data,
          base_price: data?.base_price ? wrapCurrency(data?.base_price) : data?.base_price,
          price: data?.price ? wrapCurrency(data?.price) : data?.price
        },
        index: positionInfoT2?.prodPos,
        qty_in_cart: id2qty?.[data?.id || data?.product_id],
        showActivity: showActivity,
        showWeekly: showWeekly,
        showLabel: showLabel
      },
      ctx: positionInfoT2?.context
    };
    return {
      ...impressionOption,
      impressionOptionT2: impressionOptionT2,
      id: data?.id || data?.product_id
    };
  }, [JSON.stringify(impressionOption), JSON.stringify(id2qty), JSON.stringify(positionInfoT2), JSON.stringify(data)]);

  const clickOption = useMemo(() => {
    return {
      ...(positionInfoT2?.modSecPos || {}),
      co: {
        target_pos: positionInfoT2?.prodPos,
        target_nm: data?.id || data?.product_id,
        is_mkpl: data?.biz_type === 'seller',
        recommendation_trace_id: data?.recommend_trace_id || data?.recommendation_trace_id
      },
      ctx: {
        ...positionInfoT2?.context,
        extra_attrs: getProductCardExtraAttrs(data),
        volume_price_support: data?.volume_price_support ? true : false
      }
    };
  }, [JSON.stringify(positionInfoT2), JSON.stringify(data)]);

  const { ref } = useProductCardInview(impressionOptionNew);

  const produtLink = useMemo(() => {
    let reload = false;
    if (isSayWeeeApp() || !isMobile()) {
      reload = true;
    }
    if (isInUpsell) {
      return {
        to: 'upsell',
        reload: false
      };
    }
    //bundle
    if (data?.category === 'bundle') {
      return {
        to: buildUrlWithOrigin(`/${lang}/bundle_buy/product?product_id=${data?.id}`),
        reload: true
      };
    }
    if (transformLink) {
      const _transformLink = transformLink(data);
      return {
        to: _transformLink,
        reload: reload
      };
    }
    return {
      to: buildUrlWithOrigin(
        `/${lang}/product/${data?.slug ? data?.slug : `weee/${data?.id}`}` + convertObjectToUrlParams(linkParams)
      ),
      reload: reload
    };
  }, [JSON.stringify(data), JSON.stringify(addCartParams), lang]);

  const innerNode = () => {
    switch (true) {
      case isHorizontal:
        return (
          <HorizontalCardInner
            {...props}
            className={classNames(cardInnerCls)}
            produtLink={produtLink}
            trackSaveClick={targetCollect => {
              saveClick(Object.assign(trackDataInDialog || {}, clickOption), targetCollect);
            }}
          />
        );
      default:
        return (
          <VerticalCardInner
            {...props}
            produtLink={produtLink}
            trackSaveClick={targetCollect => {
              saveClick(Object.assign(trackDataInDialog || {}, clickOption), targetCollect);
            }}
          />
        );
    }
  };

  const cls = classNames(styles.container, {
    [styles.isLink]: isLink,
    [className]: !!className,
    [styles.miniTitle]: miniTitle,
    [styles.smCard]: cardSize === 'sm',
    [styles.lightningCard]: !!isLightningCard
  });

  const trackClick = () => {
    productClick(clickOption);
  };

  if (produtLink.to === 'upsell') {
    return (
      <div className={cls} ref={ref}>
        {innerNode()}
      </div>
    );
  }

  if (produtLink.reload) {
    return (
      <a className={cls} href={produtLink.to} onClick={trackClick} ref={ref}>
        {innerNode()}
      </a>
    );
  }

  return skeleton && skeletonFactor ? (
    <>{skeleton}</>
  ) : (
    <Link prefetch={false} href={produtLink.to}>
      <a className={cls} ref={ref} onClick={trackClick}>
        {innerNode()}
      </a>
    </Link>
  );
};

ProductCard.defaultProps = {
  showFavorite: true,
  showActivity: true,
  showWeekly: true,
  showMemberPrice: true,
  showBasePrice: true,
  showExtraTag: false
};

export default ProductCard;
